import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Box, Button, Grid2, TextField, Typography, Alert, AlertTitle, IconButton, Snackbar } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { generateClient } from "aws-amplify/api";
import { listLessons } from "../../../graphql/queries";

const LessonForm = ({ initialFormState, onSave, onBack, title }) => {
  const [lesson, setLesson] = useState(initialFormState);
  const [existingOrders, setExistingOrders] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const client = useMemo(() => generateClient(), []);

  const fetchExistingOrders = useCallback(async () => {
    try {
      const result = await client.graphql({
        query: listLessons,
        variables: { limit: 1000 }
      });
      const orders = result.data.listLessons.items.map(item => ({ 
        id: item.id, 
        order: parseInt(item.order) 
      }));
      setExistingOrders(orders);
    } catch (error) {
      console.error('Error fetching existing lessons:', error);
    }
  }, [client]);

  useEffect(() => {
    fetchExistingOrders();
  }, [fetchExistingOrders]);

  // Update lesson state when initialFormState changes (for updates)
  useEffect(() => {
    setLesson(initialFormState);
  }, [initialFormState]);

  const validateField = useCallback((fieldName, value) => {
    let hasError = false;
    const newErrors = { ...errors };

    switch (fieldName) {
      case 'title':
        if (!value || !value.trim()) {
          newErrors.title = 'El título es obligatorio y no puede estar vacío';
          hasError = true;
        } else {
          delete newErrors.title;
        }
        break;
      
      case 'content':
        if (!value || !value.trim()) {
          newErrors.content = 'El contenido es obligatorio y no puede estar vacío';
          hasError = true;
        } else {
          delete newErrors.content;
        }
        break;
      
      case 'survey':
        // Survey is optional, but if it has content, it must be a valid iframe
        if (value && value.trim()) {
          const iframeRegex = /<iframe[^>]*>.*?<\/iframe>/i;
          if (!iframeRegex.test(value.trim())) {
            newErrors.survey = 'La encuesta debe contener un iframe válido';
            hasError = true;
          } else {
            delete newErrors.survey;
          }
        } else {
          // Survey is empty, which is valid
          delete newErrors.survey;
        }
        break;
      
      case 'order':
        const orderNum = parseInt(value);
        if (isNaN(orderNum) || orderNum < 1) {
          newErrors.order = 'El orden debe ser un número positivo mayor o igual a 1';
          hasError = true;
        } else {
          // Check if order exists, excluding current lesson if updating
          const orderExists = existingOrders.some(item => 
            item.order === orderNum && item.id !== lesson.id
          );
          if (orderExists) {
            newErrors.order = `El orden ${orderNum} ya existe. Elige un número disponible.`;
            hasError = true;
          } else {
            delete newErrors.order;
          }
        }
        break;
      
      default:
        break;
    }

    setErrors(newErrors);
    return !hasError;
  }, [errors, existingOrders, lesson.id]);

  const validateAllFields = () => {
    let isValid = true;
    const newErrors = {};
    
    // Validate title
    if (!lesson.title || !lesson.title.trim()) {
      newErrors.title = 'El título es obligatorio y no puede estar vacío';
      isValid = false;
    }
    
    // Validate content
    if (!lesson.content || !lesson.content.trim()) {
      newErrors.content = 'El contenido es obligatorio y no puede estar vacío';
      isValid = false;
    }
    
    // Validate order
    const orderNum = parseInt(lesson.order);
    if (isNaN(orderNum) || orderNum < 1) {
      newErrors.order = 'El orden debe ser un número positivo mayor o igual a 1';
      isValid = false;
    } else {
      // Check if order exists, excluding current lesson if updating
      const orderExists = existingOrders.some(item => 
        item.order === orderNum && item.id !== lesson.id
      );
      if (orderExists) {
        newErrors.order = `El orden ${orderNum} ya existe. Elige un número disponible.`;
        isValid = false;
      }
    }
    
    // Validate survey only if it has content
    if (lesson.survey && lesson.survey.trim()) {
      const iframeRegex = /<iframe[^>]*>.*?<\/iframe>/i;
      if (!iframeRegex.test(lesson.survey.trim())) {
        newErrors.survey = 'La encuesta debe contener un iframe válido';
        isValid = false;
      }
    }
    
    // Update errors state to show all validation errors
    setErrors(newErrors);
    
    return isValid;
  };

  const setDurationValue = (value) => {
    if (dayjs(value).isValid()) {
      const hours = dayjs(value).hour();
      const minutes = dayjs(value).minute();
      const totalMinutes = hours * 60 + minutes;
      setLesson({ ...lesson, duration: totalMinutes });
    }
  };

  const getTimePickerValue = () => {
    const hours = Math.floor(lesson.duration / 60);
    const minutes = lesson.duration % 60;
    return dayjs().hour(hours).minute(minutes).second(0).millisecond(0);
  };

  const handleFieldChange = (fieldName, value) => {
    setLesson({ ...lesson, [fieldName]: value });
    // Validate field immediately
    validateField(fieldName, value);
  };

  const handleSave = async () => {
    if (!validateAllFields()) {
      setSnackbar({
        open: true,
        message: 'Por favor corrige los errores antes de guardar',
        severity: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      await onSave(lesson);
      setSnackbar({
        open: true,
        message: 'Lección guardada exitosamente',
        severity: 'success'
      });
      // Navigate will be handled by parent component
    } catch (error) {
      console.error('Error saving lesson:', error);
      setSnackbar({
        open: true,
        message: 'Error al guardar la lección',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const renderTitle = () => {
    return (
      <Grid2 xs={12} display="flex" alignItems="center">
        <IconButton
          variant="contained"
          color="primary"
          onClick={onBack}
          sx={{ marginRight: 2 }}
        >
          <ArrowBackIcon fontSize="large"/>
        </IconButton>
        <Typography variant="h3" sx={{ marginBottom: 2 }}>{title}</Typography>
      </Grid2>
    );
  };

  const renderSaveButton = () => {
    return (
      <Grid2 xs={12} sm={2} md={2}>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={loading}
          sx={{ marginTop: 2 }}
        >
          {loading ? 'Guardando...' : 'Guardar'}
        </Button>
      </Grid2>
    );
  };

  const renderErrorBanner = () => {
    const hasErrors = Object.keys(errors).length > 0;
    if (!hasErrors) return null;

    return (
      <Alert 
        severity="error" 
        onClose={() => setErrors({})}
        sx={{ 
          marginBottom: 2, 
          width: '100%',
          backgroundColor: '#ffebee',
          borderLeft: '4px solid #f44336'
        }}
      >
        <AlertTitle>Error en el formulario</AlertTitle>
        Por favor corrige los errores marcados en los campos antes de continuar.
      </Alert>
    );
  };

  const renderForm = () => {
    return (
      <Grid2 size={12}>
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <TextField
              label="Título *"
              variant="outlined"
              fullWidth
              value={lesson.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              error={!!errors.title}
              helperText={errors.title}
              sx={{ 
                marginBottom: 2,
                '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#f44336'
                }
              }}
            />
          </Grid2>
          <Grid2 size={12}>
            <TextField
              label="Descripción"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              value={lesson.description}
              onChange={(e) => setLesson({ ...lesson, description: e.target.value })}
              sx={{ marginBottom: 2 }}
            />
          </Grid2>
          <Grid2 size={12}>
            <TextField
              label="Contenido *"
              variant="outlined"
              fullWidth
              multiline
              rows={6}
              value={lesson.content}
              onChange={(e) => handleFieldChange('content', e.target.value)}
              error={!!errors.content}
              helperText={errors.content}
              sx={{ 
                marginBottom: 2,
                '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#f44336'
                }
              }}
            />
          </Grid2>
          <Grid2 size={12}>
            <TextField
              label="Encuesta (iframe) - Opcional"
              variant="outlined"
              fullWidth
              multiline
              rows={6}
              value={lesson.survey}
              onChange={(e) => handleFieldChange('survey', e.target.value)}
              error={!!errors.survey}
              helperText={errors.survey}
              sx={{ 
                marginBottom: 2,
                '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#f44336'
                }
              }}
            />
          </Grid2>
          <Grid2 xs={12} sm={6} md={4}>
            <TextField
              label="Orden *"
              variant="outlined"
              type="number"
              fullWidth
              value={lesson.order}
              onChange={(e) => handleFieldChange('order', e.target.value)}
              error={!!errors.order}
              helperText={errors.order}
              sx={{ 
                marginBottom: 2,
                '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#f44336'
                }
              }}
            />
          </Grid2>
          <Grid2 xs={12} sm={6} md={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                label="Duración"
                variant="outlined"
                fullWidth
                value={getTimePickerValue()}
                onChange={setDurationValue}
                ampm={false} // Remove AM/PM format
                sx={{ marginBottom: 2 }}
              />
            </LocalizationProvider>
          </Grid2>
        </Grid2>
      </Grid2>
    );
  };

  return (
    <Box sx={{ padding: 2 }}>
      {renderErrorBanner()}
      <Grid2
        container
        spacing={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        {renderTitle()}
        {renderSaveButton()}
        {renderForm()}
      </Grid2>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ marginTop: 8 }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LessonForm;